import { Fragment, useContext, useEffect, useState } from "react";
import { Card, Col, Row, Table } from "react-bootstrap";
import { abpnClient } from "../Services/AbpnClient";
import { AuthContext } from "./Auth/AuthProvider";
import AbpnSpinner from "./Shared/Components/AbpnSpinner";
import { deepCopy } from "./Shared/Utility/DeepCopy";
import { getDateWithOffset } from "./Shared/Utility/GetDateWithOffset";
import { FeeType } from "./Orders/Models/Fee";
import Order, { OrderLineItem } from "./Orders/Models/Order";
import PaymentWithInvoicesOrOrdersModel from "./Shared/Models/PaymentWithInvoicesOrOrdersModel";

export default function OrdersAndInvoices() {

    const [payments, setPayments] = useState<Array<PaymentWithInvoicesOrOrdersModel>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const authContext = useContext(AuthContext);

    useEffect(() => {
        const loadOrdersAndInvoices = async () => {
            setIsLoading(true);
            const paymentInvoices = await abpnClient.get<Array<PaymentWithInvoicesOrOrdersModel>>('/api/invoice/getPaidInvoicesForPayment', authContext);
            const paymentOrders = await abpnClient.get<Array<PaymentWithInvoicesOrOrdersModel>>('/api/order/getPaidOrdersForPayment', authContext);
            const results = paymentInvoices.concat(paymentOrders);
            setPayments(results);

            let paymentDateSort = (a: PaymentWithInvoicesOrOrdersModel, b: PaymentWithInvoicesOrOrdersModel) => {
                return getDateWithOffset(b.transactionDate).getTime() - getDateWithOffset(a.transactionDate).getTime();
            }

            let sortedPayments = results.sort(paymentDateSort);
            setPayments(sortedPayments);
            setIsLoading(false);
        }

        loadOrdersAndInvoices();
    }, []);

    const getFees = (order: Order) => order.orderLineItems.filter(_ => _.feeType !== FeeType.Refund)
    const getRefunds = (order: Order) => order.orderLineItems.filter(_ => _.feeType === FeeType.Refund)
    const getFeesSubtotal = (order: Order) => getFees(order).map(_ => _.amount).reduce((a, b) => a + b, 0);
    const getRefundsSubtotal = (order: Order) => getRefunds(order).map(_ => _.amount).reduce((a, b) => a + b, 0);
    const getTotal = (order: Order) => order.orderLineItems.map(_ => _.amount).reduce((a, b) => a + b, 0);
    const getTotalPaid = (order: Order) => order.completedPayments.map(_ => _.amount).reduce((a, b) => a + b, 0);
    const getBalance = (order: Order) => getTotal(order) - getTotalPaid(order);

    const getFeeDescription = (orderLineItem: OrderLineItem) => {
        return orderLineItem.amount > 0 ? orderLineItem.description : orderLineItem.description + ' (Waived)'
    };

    const getFeeAmount = (orderLineItem: OrderLineItem) => {
        return orderLineItem.amount > 0 ? `$${Math.abs(orderLineItem.amount)}` : `- $${Math.abs(orderLineItem.amount)}`
    }

    const getSumOrderTotal = (payment: PaymentWithInvoicesOrOrdersModel) => {
        let value: number = 0;
        payment.orders.forEach(order => value += getTotal(order));
        return value;
    }

    const getSumInvoiceTotal = (payment: PaymentWithInvoicesOrOrdersModel) => {
        let value: number = 0;
        payment.invoices.forEach(invoice => value += invoice.total);
        return value;
    }

    const expandRow = (index: number) => {
        let updatedPayments: Array<PaymentWithInvoicesOrOrdersModel> = deepCopy(payments);
        updatedPayments[index].isExpanded = true;
        setPayments(updatedPayments);
    }

    const collapseRow = (index: number) => {
        let updatedPayments: Array<PaymentWithInvoicesOrOrdersModel> = deepCopy(payments);
        updatedPayments[index].isExpanded = false;
        setPayments(updatedPayments);
    }

    const download = async (paymentId: number) => {
        
        var api = `/api/payment/DownloadPaymentReceipt?paymentId=${paymentId}`;

        var response = await abpnClient.getResponse(api, authContext);
        var blob = await (response as Response).blob();

        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            `PaymentReceipt.pdf`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);
    }

    if (isLoading) return <AbpnSpinner></AbpnSpinner>
    if (payments.length === 0) return <div>There are no receipts.</div>

    return (
        <Card className="mb-3" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="font-weight-bolder">
                    <span className="text-pp-primary">Payments and Receipts</span>
                </Card.Title>
                <Row>
                    <Col xs={12} md={12}>
                        <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                            <thead>
                                <tr>
                                    <th className="mb-1 pp-table-header">Payment Date</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((_, index) =>
                                    <Fragment key={index}>
                                        <tr>
                                            <td>
                                                {getDateWithOffset(_.transactionDate).toLocaleDateString()}
                                            </td>
                                            <td>
                                                <button type="button" title="Download" className="btn btn-sm btn-clean btn-icon btn-icon-md"
                                                    onClick={()=>{download(_.paymentId)}}>
                                                    <i className="la la-download"></i>
                                                </button>
                                                {!_.isExpanded ?
                                                    <button className={`btn btn-sm btn-clean btn-icon btn-icon-md`} onClick={() => expandRow(index)}
                                                        hidden={_.isExpanded}>
                                                        <a className="font-weight-bolder mb-1 pp-font-size-smaller">Show Details</a>
                                                    </button> :
                                                    <button title="Cancel" className="btn btn-sm btn-clean btn-icon btn-icon-md" onClick={() => collapseRow(index)}>
                                                        <a className="mb-1 pp-font-size-smaller">Hide Details</a>
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                        <tr hidden={!_.isExpanded}>
                                            <Card.Body>
                                                {_.invoices?.map((invoice, invoiceIndex) =>
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th className="w-25">ID</th>
                                                                <th className="w-50">Product Description</th>
                                                                <th className="w-25">Fee</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {invoice.lineItems.map((lineItem, lineItemIndex) => {
                                                                if (lineItem.discountDescription)
                                                                    return (
                                                                        <Fragment>
                                                                            <tr key={lineItemIndex}>
                                                                                <td>{invoice.invoiceNumber}</td>
                                                                                <td>{lineItem.description}</td>
                                                                                <td>${lineItem.unitPrice}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>{invoice.invoiceNumber}</td>
                                                                                <td>{lineItem.discountDescription}</td>
                                                                                <td>- ${lineItem.discountAmount}</td>
                                                                            </tr>
                                                                        </Fragment>
                                                                    )
                                                                else
                                                                    return (
                                                                        <tr key={lineItemIndex}>
                                                                            <td>{invoice.invoiceNumber}</td>
                                                                            <td>{lineItem.description}</td>
                                                                            <td>${lineItem.unitPrice}</td>
                                                                        </tr>
                                                                    )
                                                            })}

                                                            <tr>
                                                                <td></td>
                                                                <td className="font-weight-bolder mb-1 pp-font-size-smaller">Subtotal</td>
                                                                <td className="font-weight-bolder">${invoice.total}</td>
                                                            </tr>
                                                            <tr>
                                                                <td colSpan={3}>&nbsp;</td>
                                                            </tr>

                                                            {invoiceIndex == _.invoices.length - 1 &&
                                                                <>
                                                                    <tr>
                                                                        <td colSpan={3}>&nbsp;</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Total</td>
                                                                        <td className="font-weight-bolder">${getSumInvoiceTotal(_)}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Payment</td>
                                                                        <td className="font-weight-bolder">
                                                                            {(_.paymentIncludesMultipleInvoices) ?
                                                                                <span>${_.total}*</span>
                                                                                :
                                                                                <Fragment>
                                                                                    <span>${_.total}</span>
                                                                                </Fragment>
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                    {_.paymentIncludesMultipleInvoices &&
                                                                        <tr>
                                                                            <td className="mb-1 pp-font-size-smaller" colSpan={2}>* Multiple invoices were processed in the same transaction. The displayed payment amount reflects the portion of the payment transaction that satisfied this invoice.</td>
                                                                        </tr>
                                                                    }                                                          <tr>
                                                                        <td></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Balance</td>
                                                                        <td className="font-weight-bolder">
                                                                            <span>$0</span>
                                                                        </td>
                                                                    </tr>
                                                                </>
                                                            }
                                                        </tbody>
                                                    </Table>
                                                )}

                                                {_.orders?.map((order, orderIndex) =>
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th className="w-25">ID</th>
                                                                <th className="w-50">Product Description</th>
                                                                <th className="w-25">Fee</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getFees(order).map(fee => {
                                                                return (
                                                                    <tr key={fee.orderLineItemId}>
                                                                        <td className="mb-1 pp-font-size-smaller">{order.orderNumber}</td>
                                                                        <td className="mb-1 pp-font-size-smaller">{getFeeDescription(fee)}</td>
                                                                        <td>{getFeeAmount(fee)}</td>
                                                                    </tr>
                                                                )
                                                            })}
                                                            <tr>
                                                                <td className="mb-1 pp-font-size-smaller"></td>
                                                                <td className="font-weight-bolder mb-1 pp-font-size-smaller">Subtotal</td>
                                                                <td className="font-weight-bolder">${getFeesSubtotal(order)}</td>
                                                            </tr>
                                                            {getRefunds(order).map(refund => {
                                                                return (
                                                                    <tr key={refund.orderLineItemId}>
                                                                        <td className="mb-1 pp-font-size-smaller">Refunded</td>
                                                                        <td className="mb-1 pp-font-size-smaller">{getDateWithOffset(refund.createdDate).toLocaleDateString()}</td>
                                                                        <td>${refund.amount}</td>
                                                                    </tr>
                                                                )
                                                            })}
                                                            {getRefunds(order).length > 0 &&
                                                                <>
                                                                    <tr>
                                                                        <td className="mb-1 pp-font-size-smaller"></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Refunded</td>
                                                                        <td className="font-weight-bolder">${getRefundsSubtotal(order)}</td>
                                                                    </tr>
                                                                </>
                                                            }

                                                            {order.installments?.map((installment, installmentIndex) => {
                                                                return (
                                                                    <tr key={installmentIndex}>
                                                                        <td className="mb-1 pp-font-size-smaller"></td>
                                                                        <td className="mb-1 pp-font-size-smaller">Due by {getDateWithOffset(installment.dueDate).toLocaleDateString()}</td>
                                                                        <td>${installment.amount}</td>
                                                                    </tr>
                                                                )
                                                            })}
                                                            {orderIndex == _.orders.length - 1 &&
                                                                <>
                                                                    <tr>
                                                                        <td colSpan={3}>&nbsp;</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Total</td>
                                                                        <td className="font-weight-bolder">${getSumOrderTotal(_)}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Payment</td>
                                                                        <td className="font-weight-bolder">${_.total}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="mb-1 pp-font-size-smaller"></td>
                                                                        <td className="font-weight-bolder mb-1 pp-font-size-smaller">Balance</td>
                                                                        <td className="font-weight-bolder">${getBalance(order)}</td>
                                                                    </tr>
                                                                </>
                                                            }
                                                        </tbody>
                                                    </Table>
                                                )}
                                            </Card.Body>
                                        </tr>
                                    </Fragment>
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}